// PipelineProcessFlow — simplified 5-phase FIFO pipeline that always narrows.
// Replaces the previous 30-step waterfall on the Overview page.
// Phases: Interest → Application Process → BOD Vote → Recruit Training → Year 1 Active

import { useState } from "react";
import {
  Users,
  ClipboardList,
  Vote,
  GraduationCap,
  ShieldCheck,
  ChevronRight,
  AlertTriangle,
  Info,
  XCircle,
  AlertCircle,
} from "lucide-react";
import type { WaterfallStepRow } from "../../hooks/useForge";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineProcessFlowProps {
  /** Raw aggregate key→value map from the aggregates endpoint */
  aggregates?: Record<string, unknown>;
  /** Kept for API compatibility — not used for display in the new design */
  steps?: WaterfallStepRow[];
  period?: string;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

// ── Phase key type ────────────────────────────────────────────────────────────

type PhaseKey = "interest" | "application" | "bod" | "training" | "year1";

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

// ── Color palette per phase — muted, professional ────────────────────────────

const PHASE_COLORS: Record<
  PhaseKey,
  {
    border: string;
    iconBg: string;
    iconColor: string;
    labelColor: string;
  }
> = {
  interest: {
    border: "border-t-slate-400",
    iconBg: "bg-slate-100 dark:bg-slate-800",
    iconColor: "text-slate-500 dark:text-slate-400",
    labelColor: "text-slate-700 dark:text-slate-300",
  },
  application: {
    border: "border-t-teal-500",
    iconBg: "bg-teal-50 dark:bg-teal-900/30",
    iconColor: "text-teal-600 dark:text-teal-400",
    labelColor: "text-teal-700 dark:text-teal-400",
  },
  bod: {
    border: "border-t-emerald-600",
    iconBg: "bg-emerald-50 dark:bg-emerald-900/30",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    labelColor: "text-emerald-700 dark:text-emerald-400",
  },
  training: {
    border: "border-t-sky-600",
    iconBg: "bg-sky-50 dark:bg-sky-900/30",
    iconColor: "text-sky-600 dark:text-sky-400",
    labelColor: "text-sky-700 dark:text-sky-400",
  },
  year1: {
    border: "border-t-amber-500",
    iconBg: "bg-amber-50 dark:bg-amber-900/30",
    iconColor: "text-amber-600 dark:text-amber-400",
    labelColor: "text-amber-700 dark:text-amber-400",
  },
};

// ── Tooltip ───────────────────────────────────────────────────────────────────

function Tooltip({
  content,
  children,
}: {
  content: string;
  children: React.ReactNode;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}
      {visible && (
        <span
          role="tooltip"
          className={[
            "absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2",
            "w-72 rounded-lg border border-zinc-200 dark:border-zinc-700",
            "bg-white dark:bg-zinc-800 shadow-lg px-3 py-2",
            "text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed",
            "pointer-events-none",
          ].join(" ")}
        >
          {content}
        </span>
      )}
    </span>
  );
}

// ── Shared stats row — every phase card renders exactly three stats so the
// cards are the same height regardless of which phase has rich outcome data.
//
// Tone palette keeps the colors meaningful (approved=green, fail=amber,
// declined=red, neutral=zinc) so the BOD card still conveys outcome
// sentiment and the other cards don't look empty.

type StatTone = "emerald" | "amber" | "red" | "sky" | "zinc";

interface PhaseStat {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: StatTone;
}

const TONE_CLASSES: Record<
  StatTone,
  { bg: string; text: string; border: string }
> = {
  emerald: {
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    border: "border-emerald-200 dark:border-emerald-800/40",
  },
  amber: {
    bg: "bg-amber-50 dark:bg-amber-900/20",
    text: "text-amber-700 dark:text-amber-300",
    border: "border-amber-200 dark:border-amber-800/40",
  },
  red: {
    bg: "bg-red-50 dark:bg-red-900/20",
    text: "text-red-700 dark:text-red-300",
    border: "border-red-200 dark:border-red-800/40",
  },
  sky: {
    bg: "bg-sky-50 dark:bg-sky-900/20",
    text: "text-sky-700 dark:text-sky-300",
    border: "border-sky-200 dark:border-sky-800/40",
  },
  zinc: {
    bg: "bg-zinc-100 dark:bg-zinc-800/40",
    text: "text-zinc-700 dark:text-zinc-300",
    border: "border-zinc-200 dark:border-zinc-700",
  },
};

function PhaseStatsRow({
  stats,
  footnote,
}: {
  stats: PhaseStat[];
  footnote?: string;
}) {
  return (
    <div className="mt-3 space-y-1.5">
      {footnote && (
        <p className="text-[10px] text-zinc-500 dark:text-zinc-400 italic mb-1">
          {footnote}
        </p>
      )}
      {stats.map((s, i) => {
        const t = TONE_CLASSES[s.tone];
        return (
          <div
            key={i}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md ${t.bg} border ${t.border} min-w-0`}
            title={`${s.label}: ${s.value}`}
          >
            <span className={`${t.text} shrink-0 flex`}>{s.icon}</span>
            {/* Labels wrap onto 2 lines at tight widths instead of silently
                truncating. `leading-tight` keeps the row ~same height even
                when wrapped; `break-words` defeats the default no-break on
                flex children. */}
            <span
              className={`text-[11px] ${t.text} font-semibold flex-1 min-w-0 leading-tight break-words`}
            >
              {s.label}
            </span>
            <span
              className={`text-xs font-bold tabular-nums ${t.text} shrink-0`}
            >
              {s.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Phase connector — arrow between phases ────────────────────────────────────

function PhaseConnector({
  dropLabel,
  tooltip,
}: {
  dropLabel?: string;
  tooltip?: string;
}) {
  return (
    <div className="hidden md:flex flex-col items-center justify-center shrink-0 px-0.5 gap-1 select-none">
      <ChevronRight size={16} className="text-zinc-300 dark:text-zinc-600" />
      {dropLabel && (
        <Tooltip content={tooltip ?? dropLabel}>
          <span className="cursor-help text-[11px] text-zinc-400 dark:text-zinc-500 font-medium whitespace-nowrap tabular-nums">
            {dropLabel}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

// ── Phase card ────────────────────────────────────────────────────────────────

interface PhaseCardProps {
  phaseKey: PhaseKey;
  label: string;
  subtitle: string;
  contextLabel: string;
  count: number | null;
  countNote?: string; // shown under count, e.g. "estimate"
  icon: React.ReactNode;
  /** Three stat pills so every card has the same vertical footprint. */
  stats?: PhaseStat[];
  statsFootnote?: string;
  isLoading?: boolean;
}

function PhaseCard({
  phaseKey,
  label,
  subtitle,
  contextLabel,
  count,
  countNote,
  icon,
  stats,
  statsFootnote,
  isLoading,
}: PhaseCardProps) {
  const colors = PHASE_COLORS[phaseKey];

  return (
    <div
      className={[
        "flex-1 min-w-[130px] rounded-xl border-2 border-t-4 p-4 md:p-5",
        "flex flex-col bg-zinc-50 dark:bg-zinc-900 shadow-sm",
        colors.border,
        "border-zinc-200 dark:border-zinc-700",
      ].join(" ")}
    >
      {/* Icon + label */}
      <div className="flex items-center gap-2 mb-0.5">
        <span className={`p-1 rounded-md ${colors.iconBg}`}>
          <span className={colors.iconColor}>{icon}</span>
        </span>
        <span
          className={`text-xs font-semibold tracking-wider uppercase ${colors.labelColor}`}
        >
          {label}
        </span>
      </div>

      {/* Subtitle */}
      <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mt-1 mb-3">
        {subtitle}
      </p>

      {/* Headline number */}
      <div className="mb-0.5">
        {isLoading ? (
          <div className="skeleton h-10 w-20 rounded-lg" />
        ) : (
          <span
            className={`text-3xl md:text-4xl font-semibold tabular-nums tracking-tight leading-none ${
              count !== null && count > 0
                ? "text-zinc-900 dark:text-zinc-50"
                : "text-zinc-400 dark:text-zinc-600"
            }`}
          >
            {count !== null && count > 0 ? count.toLocaleString() : "—"}
          </span>
        )}
      </div>

      {/* Context label */}
      <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-snug mb-1">
        {contextLabel}
      </p>

      {/* Count note (estimate label, etc.) */}
      {countNote && (
        <p className="text-[11px] text-zinc-400 dark:text-zinc-500 italic mb-1">
          {countNote}
        </p>
      )}

      {/* Stats slot — each card renders exactly 3 pills so heights match.
          Pushed to the bottom via mt-auto so cards with short context labels
          still share a visual rhythm with cards that have longer context. */}
      <div className="mt-auto">
        {stats && stats.length > 0 && (
          <PhaseStatsRow stats={stats} footnote={statsFootnote} />
        )}
      </div>
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function ProcessFlowSkeleton() {
  return (
    <div className="flex gap-2 overflow-hidden">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2 flex-1 min-w-0">
          <div className="skeleton flex-1 h-52 rounded-xl" />
          {i < 4 && <div className="skeleton h-4 w-4 rounded-full shrink-0" />}
        </div>
      ))}
    </div>
  );
}

// ── Error state ───────────────────────────────────────────────────────────────

function ProcessFlowError({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
        <AlertTriangle size={20} className="text-red-500" />
      </div>
      <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
        Failed to load pipeline data
      </p>
      <p className="text-xs text-zinc-400 max-w-xs text-center">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 px-4 py-2 rounded-lg text-xs font-semibold bg-[#0E403A] text-white hover:opacity-90 transition-opacity"
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PipelineProcessFlow({
  aggregates = {},
  isLoading = false,
  error,
  onRetry,
}: PipelineProcessFlowProps) {
  if (error) return <ProcessFlowError message={error} onRetry={onRetry} />;
  if (isLoading) return <ProcessFlowSkeleton />;

  // ── Extract aggregate values ──────────────────────────────────────────────
  // Every phase card has the same metric structure so leaders can read the
  // pipeline as a real funnel: count-entered, conversion-to-next, YoY trend,
  // top dropoff. All values come from real dash_aggregates keys; missing
  // values render as em-dash, never as fabricated estimates.

  const webVisitorsYtd = asNumber(aggregates["web_visitors_ytd"]);
  const webVisitorsPriorYtd = asNumber(aggregates["web_visitors_prior_ytd"]);
  const typeformYtd = asNumber(aggregates["typeform_submissions_ytd"]);
  const typeformPriorYtd = asNumber(
    aggregates["typeform_submissions_prior_ytd"],
  );
  const applicantYtd = asNumber(aggregates["applicant_count_ytd"]);
  // bod_total_reached_ytd is the YTD denominator for the YTD approval-rate
  // pill — without it, rates mix YTD numerator with all-time denominator.
  const bodTotalReachedYtd = asNumber(aggregates["bod_total_reached_ytd"]);
  const bodApprovedYtd = asNumber(aggregates["bod_approved_ytd"]);
  const bodApprovedPriorYtd = asNumber(aggregates["bod_approved_prior_ytd"]);
  const trainingCompletedYtd = asNumber(aggregates["training_completed_ytd"]);
  const trainingCompletedPriorYtd = asNumber(
    aggregates["training_completed_prior_ytd"],
  );
  const firstYearRetentionRate = asNumber(
    aggregates["first_year_retention_rate"],
  );
  const firstYearRetentionRatePrior = asNumber(
    aggregates["first_year_retention_rate_prior"],
  );
  const firstYearSurvivors = asNumber(
    aggregates["first_year_retention_survivors"],
  );
  const firstYearSurvivorsPrior = asNumber(
    aggregates["first_year_retention_survivors_prior"],
  );
  const activeMemberCount = asNumber(aggregates["active_member_count"]);
  const pipelineActive = asNumber(aggregates["pipeline_active_current"]);

  // Headlines: count entered each phase YTD. Null when the underlying
  // aggregate hasn't been computed yet (e.g. CF visitors before the next
  // sync runs) so the card honestly renders as "—" instead of a fake number.
  const interestCount = webVisitorsYtd > 0 ? webVisitorsYtd : null;
  const applicationCount = typeformYtd > 0 ? typeformYtd : null;
  const bodCount = bodApprovedYtd > 0 ? bodApprovedYtd : null;
  const trainingCount = trainingCompletedYtd > 0 ? trainingCompletedYtd : null;
  const year1Count = firstYearSurvivors > 0 ? firstYearSurvivors : null;

  const hasData = applicationCount !== null || pipelineActive > 0;

  // ── Formatters ──────────────────────────────────────────────────────────
  const fmtNum = (n: number): string => (n > 0 ? n.toLocaleString() : "—");
  const fmtPct = (p: number): string =>
    p > 0 ? `${Math.round(p * 100)}%` : "—";
  const fmtPct1 = (p: number): string =>
    p > 0 ? `${(p * 100).toFixed(1)}%` : "—";
  const fmtSignedPct = (current: number, prior: number): string => {
    if (prior <= 0) return "—";
    const delta = ((current - prior) / prior) * 100;
    const sign = delta >= 0 ? "+" : "";
    return `${sign}${delta.toFixed(0)}%`;
  };
  const trendTone = (current: number, prior: number): StatTone => {
    if (prior <= 0) return "zinc";
    return current >= prior ? "emerald" : "red";
  };

  // ── Top-dropoff lookups (sourced from dash_aggregates outcomes) ─────────
  // Application → BOD: dropped_before_ais is the dominant loss per the
  // honest drop-off analysis (PR #58). Until /api/forge/drop-off feeds these
  // numbers into aggregates, we surface the highest-impact category as a
  // fixed label with the available count from pipeline_stale_contacts as a
  // proxy. Honest fallback: hide the pill when no data is available.
  const droppedBeforeAis = asNumber(aggregates["drop_off_before_ais"]);
  const aisNoShow = asNumber(aggregates["drop_off_ais_no_show"]);
  const physicalFail = asNumber(aggregates["bod_physical_fail_total"]);
  const bodDeclinedTotal = asNumber(aggregates["bod_declined_total"]);

  // ── Standardized 3-pill stat sets per phase ─────────────────────────────
  // Pattern: [conversion-to-next, YoY trend, top-dropoff-or-context]

  const interestStats: PhaseStat[] = [
    {
      icon: <ChevronRight size={10} />,
      label: "Apply rate",
      value:
        webVisitorsYtd > 0 && typeformYtd > 0
          ? fmtPct1(typeformYtd / webVisitorsYtd)
          : "—",
      tone: webVisitorsYtd > 0 && typeformYtd > 0 ? "sky" : "zinc",
    },
    {
      icon: <Users size={10} />,
      label: "YoY visitors",
      value: fmtSignedPct(webVisitorsYtd, webVisitorsPriorYtd),
      tone: trendTone(webVisitorsYtd, webVisitorsPriorYtd),
    },
    {
      icon: <Info size={10} />,
      label: "Source",
      value: "CF zone",
      tone: "zinc",
    },
  ];

  const applicationStats: PhaseStat[] = [
    {
      icon: <ChevronRight size={10} />,
      label: "Reach BOD rate",
      // YTD numerator over YTD denominator. Both come from the same calendar
      // window, so the rate is honest. Hidden when YTD denominator is empty
      // (e.g. early January) to avoid div-by-near-zero noise.
      value:
        applicantYtd > 0 && bodTotalReachedYtd > 0
          ? fmtPct(bodTotalReachedYtd / applicantYtd)
          : "—",
      tone: "sky",
    },
    {
      icon: <ClipboardList size={10} />,
      label: "YoY applications",
      value: fmtSignedPct(typeformYtd, typeformPriorYtd),
      tone: trendTone(typeformYtd, typeformPriorYtd),
    },
    {
      icon: <AlertCircle size={10} />,
      label: "Top drop: pre-AIS",
      value: droppedBeforeAis > 0 ? fmtNum(droppedBeforeAis) : "—",
      tone: droppedBeforeAis > 0 ? "amber" : "zinc",
    },
  ];

  const bodStats: PhaseStat[] = [
    {
      icon: <ChevronRight size={10} />,
      label: "Approval rate (YTD)",
      // Both YTD — see bod_total_reached_ytd in dashboard-sync.ts
      value:
        bodTotalReachedYtd > 0
          ? fmtPct(bodApprovedYtd / bodTotalReachedYtd)
          : "—",
      tone: "emerald",
    },
    {
      icon: <Vote size={10} />,
      label: "YoY approvals",
      value: fmtSignedPct(bodApprovedYtd, bodApprovedPriorYtd),
      tone: trendTone(bodApprovedYtd, bodApprovedPriorYtd),
    },
    {
      icon: <XCircle size={10} />,
      label: "Declined / phys fail",
      value:
        bodDeclinedTotal + physicalFail > 0
          ? `${bodDeclinedTotal + physicalFail}`
          : "—",
      tone: "amber",
    },
  ];

  const trainingStats: PhaseStat[] = [
    {
      icon: <ChevronRight size={10} />,
      label: "Completion rate",
      value: fmtPct(asNumber(aggregates["training_completion_rate"])),
      tone: "sky",
    },
    {
      icon: <GraduationCap size={10} />,
      label: "YoY graduates",
      value: fmtSignedPct(trainingCompletedYtd, trainingCompletedPriorYtd),
      tone: trendTone(trainingCompletedYtd, trainingCompletedPriorYtd),
    },
    {
      icon: <AlertCircle size={10} />,
      label: "AIS no-shows",
      value: aisNoShow > 0 ? fmtNum(aisNoShow) : "—",
      tone: aisNoShow > 0 ? "amber" : "zinc",
    },
  ];

  const year1Stats: PhaseStat[] = [
    {
      icon: <ChevronRight size={10} />,
      label: "Year 1 retention",
      value: fmtPct(firstYearRetentionRate),
      tone:
        firstYearRetentionRate >= 0.5
          ? "emerald"
          : firstYearRetentionRate > 0
            ? "amber"
            : "zinc",
    },
    {
      icon: <ShieldCheck size={10} />,
      label: "YoY retention",
      value:
        firstYearRetentionRate > 0 && firstYearRetentionRatePrior > 0
          ? fmtSignedPct(firstYearRetentionRate, firstYearRetentionRatePrior)
          : fmtSignedPct(firstYearSurvivors, firstYearSurvivorsPrior),
      tone:
        firstYearRetentionRate >= firstYearRetentionRatePrior
          ? "emerald"
          : "red",
    },
    {
      icon: <Users size={10} />,
      label: "Active total",
      value: fmtNum(activeMemberCount),
      tone:
        activeMemberCount >= 200
          ? "emerald"
          : activeMemberCount > 0
            ? "amber"
            : "zinc",
    },
  ];

  // Summary info
  const totalTooltip =
    "FORGE Pipeline (YTD): each phase shows the count entered this year, " +
    "conversion to the next phase, YoY trend vs the same period last year, " +
    "and the top dropoff for that phase. All numbers come from real syncs — " +
    "web visitors from Cloudflare zone analytics, applications from Typeform, " +
    "BOD outcomes from the applicant table, training from the recruit-training " +
    "table, year-1 retention from the member roster.";

  return (
    <div className="space-y-5">
      {/* ── Summary row ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap text-xs text-zinc-400 dark:text-zinc-500">
        <span className="tabular-nums">
          <span className="font-bold text-zinc-800 dark:text-zinc-200">
            {webVisitorsYtd > 0 ? webVisitorsYtd.toLocaleString() : "—"}
          </span>{" "}
          web sessions YTD
        </span>
        <span className="hidden sm:inline text-zinc-300 dark:text-zinc-600">
          ·
        </span>
        <span className="tabular-nums hidden sm:inline">
          <span className="font-bold text-[#16A34A] dark:text-green-400">
            {typeformYtd > 0 ? typeformYtd.toLocaleString() : "—"}
          </span>{" "}
          applications YTD
        </span>
        <span className="hidden sm:inline text-zinc-300 dark:text-zinc-600">
          ·
        </span>
        <span className="tabular-nums hidden sm:inline">
          <span className="font-bold text-emerald-600 dark:text-emerald-400">
            {bodApprovedYtd > 0 ? bodApprovedYtd.toLocaleString() : "—"}
          </span>{" "}
          BOD approvals YTD
        </span>

        {/* Global help */}
        <Tooltip content={totalTooltip}>
          <span className="ml-auto flex items-center gap-1 cursor-help text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">
            <Info size={13} />
            <span className="text-[10px] font-medium">How to read this</span>
          </span>
        </Tooltip>
      </div>

      {/* ── Phase flow — horizontal on md+, vertical on mobile ───────── */}
      <div className="hidden md:flex items-stretch gap-1.5 overflow-x-auto pb-1">
        {/* Phase 1: Interest */}
        <PhaseCard
          phaseKey="interest"
          label="INTEREST"
          subtitle="Web visits YTD"
          contextLabel="dcvfd.org daily-unique visitor sessions, summed YTD"
          count={interestCount}
          countNote={webVisitorsYtd === 0 ? "CF data syncing" : undefined}
          icon={<Users size={16} strokeWidth={1.8} />}
          stats={interestStats}
        />

        <PhaseConnector
          dropLabel="apply"
          tooltip="Visitors who submitted the Typeform application. Apply rate is the conversion: typeform_ytd / web_visits_ytd."
        />

        {/* Phase 2: Application Process */}
        <PhaseCard
          phaseKey="application"
          label="APPLICATION"
          subtitle="Submissions YTD"
          contextLabel="Typeform applications received this year"
          count={applicationCount}
          isLoading={!hasData && isLoading}
          icon={<ClipboardList size={16} strokeWidth={1.8} />}
          stats={applicationStats}
        />

        <PhaseConnector
          dropLabel="reach BOD"
          tooltip="Applicants who complete screening, physical, and background check advance to the Board of Directors vote."
        />

        {/* Phase 3: BOD Vote */}
        <PhaseCard
          phaseKey="bod"
          label="BOD VOTE"
          subtitle="Approvals YTD"
          contextLabel="board-approved this year"
          count={bodCount}
          icon={<Vote size={16} strokeWidth={1.8} />}
          stats={bodStats}
        />

        <PhaseConnector
          dropLabel="training"
          tooltip="BOD-approved recruits enter training. The Training count below is YTD graduates (Released for Assignment), not the current cohort size."
        />

        {/* Phase 4: Recruit Training */}
        <PhaseCard
          phaseKey="training"
          label="TRAINING"
          subtitle="Graduates YTD"
          contextLabel="recruits released for assignment this year"
          count={trainingCount}
          isLoading={!hasData && isLoading}
          icon={<GraduationCap size={16} strokeWidth={1.8} />}
          stats={trainingStats}
        />

        <PhaseConnector
          dropLabel="1yr active"
          tooltip="Members who joined 12-24 months ago and are still in an active status today — the cohort that 'survived' year 1."
        />

        {/* Phase 5: Year 1 Active */}
        <PhaseCard
          phaseKey="year1"
          label="YEAR 1 ACTIVE"
          subtitle="Past 1-yr cohort"
          contextLabel="joined 12-24mo ago, still active"
          count={year1Count}
          isLoading={!hasData && isLoading}
          icon={<ShieldCheck size={16} strokeWidth={1.8} />}
          stats={year1Stats}
          statsFootnote="Prior YoY = 24–36 mo cohort still active today (understates baseline)"
        />
      </div>

      {/* Mobile: vertical stack */}
      <div className="md:hidden space-y-2">
        <PhaseCard
          phaseKey="interest"
          label="INTEREST"
          subtitle="Web visits YTD"
          contextLabel="dcvfd.org daily-unique visitor sessions, summed YTD"
          count={interestCount}
          countNote={webVisitorsYtd === 0 ? "CF data syncing" : undefined}
          icon={<Users size={16} strokeWidth={1.8} />}
          stats={interestStats}
        />
        <div className="flex justify-center text-zinc-300 dark:text-zinc-600">
          <ChevronRight size={16} className="rotate-90" />
        </div>
        <PhaseCard
          phaseKey="application"
          label="APPLICATION"
          subtitle="Submissions YTD"
          contextLabel="Typeform applications received this year"
          count={applicationCount}
          icon={<ClipboardList size={16} strokeWidth={1.8} />}
          stats={applicationStats}
        />
        <div className="flex justify-center text-zinc-300 dark:text-zinc-600">
          <ChevronRight size={16} className="rotate-90" />
        </div>
        <PhaseCard
          phaseKey="bod"
          label="BOD VOTE"
          subtitle="Approvals YTD"
          contextLabel="board-approved this year"
          count={bodCount}
          icon={<Vote size={16} strokeWidth={1.8} />}
          stats={bodStats}
        />
        <div className="flex justify-center text-zinc-300 dark:text-zinc-600">
          <ChevronRight size={16} className="rotate-90" />
        </div>
        <PhaseCard
          phaseKey="training"
          label="TRAINING"
          subtitle="Graduates YTD"
          contextLabel="recruits released for assignment this year"
          count={trainingCount}
          icon={<GraduationCap size={16} strokeWidth={1.8} />}
          stats={trainingStats}
        />
        <div className="flex justify-center text-zinc-300 dark:text-zinc-600">
          <ChevronRight size={16} className="rotate-90" />
        </div>
        <PhaseCard
          phaseKey="year1"
          label="YEAR 1 ACTIVE"
          subtitle="Past 1-yr cohort"
          contextLabel="joined 12-24mo ago, still active"
          count={year1Count}
          icon={<ShieldCheck size={16} strokeWidth={1.8} />}
          stats={year1Stats}
          statsFootnote="Prior YoY = 24–36 mo cohort still active today (understates baseline)"
        />
      </div>
    </div>
  );
}
